import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface SessionTimeoutDialogProps {
  open: boolean;
  /** How many seconds the user has to respond before auto-logout */
  countdownSeconds: number;
  onStayLoggedIn: () => void;
  onLogOut: () => void;
}

const SessionTimeoutDialog: React.FC<SessionTimeoutDialogProps> = ({
  open,
  countdownSeconds,
  onStayLoggedIn,
  onLogOut,
}) => {
  const safeCountdownSeconds = Math.max(1, Math.floor(countdownSeconds));
  const [remaining, setRemaining] = useState(safeCountdownSeconds);

  // Reset countdown whenever dialog opens
  useEffect(() => {
    if (!open) return;
    setRemaining(safeCountdownSeconds);

    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, safeCountdownSeconds]);

  const formatTime = useCallback((seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
  }, []);

  const rawProgress = (remaining / safeCountdownSeconds) * 100;
  const progress = Math.max(0, Math.min(100, Number.isFinite(rawProgress) ? rawProgress : 0));

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden' },
      }}
    >
      {/* Progress bar at top — drains as countdown decreases */}
      <LinearProgress
        variant="determinate"
        value={progress}
        color={remaining <= 30 ? 'error' : 'warning'}
        sx={{ height: 4 }}
      />

      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Session Expiring Soon
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          You've been inactive for a while. For your security, you'll be automatically
          logged out in:
        </Typography>
        <Typography
          variant="h3"
          align="center"
          fontWeight={700}
          color={remaining <= 30 ? 'error.main' : 'warning.main'}
          sx={{ my: 2 }}
        >
          {formatTime(remaining)}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Click "Stay Logged In" to continue your session.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onLogOut}
          sx={{ flex: 1 }}
        >
          Log Out
        </Button>
        <Button
          variant="contained"
          onClick={onStayLoggedIn}
          autoFocus
          sx={{
            flex: 1,
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
            },
          }}
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutDialog;
